// synced from loomweb (v6 packages/loom-block-render/src/block-renderer.tsx).
/**
 * Render a single loom MessageBlock through the ported loomweb view for its
 * discriminant `type`. Mirrors loomweb's own type->view switch, inlined to the
 * set this replay proof exercises (text / command / file-* / todo / skill /
 * web-search / knowledge-query plus the remote-* family). For any still-unported
 * type, the default branch degrades gracefully: it salvages whatever human text
 * the block carries (content / text / command / query / output string fields)
 * into a minimal panel with a small dim "(<type>)" tag, so a future block type
 * never shows a content-less placeholder.
 */
import type { JSX } from "react";
import type { MessageBlock } from "@shared/loom-message-block";
import { TextBlockView } from "./text-block-view";
import { CommandBlockView } from "./command-block-view";
import { FileEditBlockView } from "./file-edit-block-view";
import { FileReadBlockView } from "./file-read-block-view";
import { FileSearchBlockView } from "./file-search-block-view";
import { TodoBlockView } from "./todo-block-view";
import { SkillBlockView } from "./skill-block-view";
import { WebSearchBlockView } from "./web-search-block-view";
import { KnowledgeQueryBlockView } from "./knowledge-query-block-view";
import { RemoteCommandBlockView } from "./remote-command-block-view";
import { RemoteQueryBlockView } from "./remote-query-block-view";
import { RemoteBashBlockView } from "./remote-bash-block-view";
import { RemoteFileTransferBlockView } from "./remote-file-transfer-block-view";

/** String-valued fields, in priority order, that may carry human-readable text. */
const FALLBACK_TEXT_FIELDS = ["content", "text", "message", "query", "command"] as const;

/**
 * Best-effort extraction of any human-readable text from an unknown block, used by
 * the default fallback so unported types still surface their content. Checks the
 * common string fields first, then a nested `result.output` / `output` string.
 */
function extractFallbackText(block: MessageBlock): string {
  const record = block as unknown as Record<string, unknown>;
  for (const field of FALLBACK_TEXT_FIELDS) {
    const value = record[field];
    if (typeof value === "string" && value.length > 0) return value;
  }
  const result = record.result;
  if (typeof result === "object" && result !== null) {
    const nestedOutput = (result as Record<string, unknown>).output;
    if (typeof nestedOutput === "string" && nestedOutput.length > 0) return nestedOutput;
  }
  const output = record.output;
  if (typeof output === "string" && output.length > 0) return output;
  return "";
}

export function BlockRenderer({ block }: { block: MessageBlock }): JSX.Element {
  switch (block.type) {
    case "text":
      return <TextBlockView block={block} />;
    case "command":
      return <CommandBlockView block={block} />;
    case "file-edit":
      return <FileEditBlockView block={block} />;
    // file-write shares the file-edit view shape; render it with the same view.
    case "file-write":
      return (
        <FileEditBlockView
          block={{ ...block, type: "file-edit" }}
        />
      );
    case "file-read":
      return <FileReadBlockView block={block} />;
    case "file-search":
      return <FileSearchBlockView block={block} />;
    case "todo":
      return <TodoBlockView block={block} />;
    case "skill":
      return <SkillBlockView block={block} />;
    case "web-search":
      return <WebSearchBlockView block={block} />;
    case "knowledge-query":
      return <KnowledgeQueryBlockView block={block} />;
    case "remote-command":
      return <RemoteCommandBlockView block={block} />;
    case "remote-query":
      return <RemoteQueryBlockView block={block} />;
    case "remote-bash":
      return <RemoteBashBlockView block={block} />;
    case "remote-file-transfer":
      return <RemoteFileTransferBlockView block={block} />;
    default: {
      // Graceful fallback: surface any salvageable text instead of a blank
      // "unsupported" placeholder; keep a dim type tag for diagnosis.
      const text = extractFallbackText(block);
      return (
        <div className="replay-unsupported">
          {text.length > 0 ? (
            <pre className="replay-unsupported-body">{text}</pre>
          ) : (
            <span className="replay-text-muted">无可显示内容</span>
          )}
          <span className="replay-unsupported-tag">({block.type})</span>
        </div>
      );
    }
  }
}
